import { Injectable, OnDestroy, Renderer2 } from "@angular/core";

@Injectable()
export class HoverPanelService implements OnDestroy {
  private readonly hidePanelDelayMs = 300;
  private readonly hidePanelTimers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
  private readonly openPanels = new Map<HTMLElement, { trigger: HTMLElement; sideAnchor?: HTMLElement }>();
  private readonly repositionOpenPanels = (): void => {
    this.openPanels.forEach(({ trigger, sideAnchor }, panel) => this.positionPanel(panel, trigger, sideAnchor));
  }

  constructor(private readonly renderer: Renderer2) {

  }

  // A pure CSS :hover show/hide (display: block only while the trigger or panel is hovered) drops
  // the moment the pointer strays off the trigger's thin text box while crossing the gap to reach
  // the panel below it - once display: none, that panel can't be re-entered, so the mouse can never
  // land on it to use its scrollbar. Controlling display from JS with a short hide grace period
  // (hover-intent) tolerates that gap and keeps the panel open while it or its trigger is hovered,
  // including while a nested tooltip inside the "+more" popover is open (sideAnchor).
  show(event: MouseEvent | FocusEvent, panel: HTMLElement, sideAnchor?: HTMLElement): void {
    this.cancelHidePanel(panel);
    if (sideAnchor) {
      this.cancelHidePanel(sideAnchor);
    }
    const trigger = event.currentTarget as HTMLElement;
    // Panel must be visible before positionPanel measures its natural size below/right of the trigger.
    this.renderer.setStyle(panel, "display", "block");
    this.positionPanel(panel, trigger, sideAnchor);
    this.trackOpenPanel(panel, trigger, sideAnchor);
  }

  keepOpen(panel: HTMLElement, sideAnchor?: HTMLElement): void {
    this.cancelHidePanel(panel);
    if (sideAnchor) {
      this.cancelHidePanel(sideAnchor);
    }
  }

  scheduleHide(panel: HTMLElement): void {
    this.cancelHidePanel(panel);
    this.hidePanelTimers.set(panel, setTimeout(() => {
      this.renderer.setStyle(panel, "display", "none");
      this.hidePanelTimers.delete(panel);
      this.untrackOpenPanel(panel);
    },                                         this.hidePanelDelayMs));
  }

  // Grid row templates (and their #panel template refs) get torn down and recreated whenever the
  // grid reloads (paging, sorting, filtering) - if that happens mid-hover, the old panel/trigger
  // elements never fire mouseleave/blur, so their timers and open-panel tracking would otherwise
  // leak until the whole component is destroyed. Call this whenever the grid's row data is replaced.
  reset(): void {
    this.hidePanelTimers.forEach(clearTimeout);
    this.hidePanelTimers.clear();
    window.removeEventListener("scroll", this.repositionOpenPanels, true);
    window.removeEventListener("resize", this.repositionOpenPanels);
    this.openPanels.clear();
  }

  ngOnDestroy(): void {
    this.reset();
  }

  private cancelHidePanel(panel: HTMLElement): void {
    const itemTimer = this.hidePanelTimers.get(panel);
    if (itemTimer) {
      clearTimeout(itemTimer);
      this.hidePanelTimers.delete(panel);
    }
  }

  private positionPanel(panel: HTMLElement, trigger: HTMLElement, sideAnchor?: HTMLElement): void {
    const panelRect = panel.getBoundingClientRect();
    const position = sideAnchor
      ? this.getSideAnchoredPosition(trigger, sideAnchor, panelRect)
      : this.getBelowTriggerPosition(trigger, panelRect);
    this.renderer.setStyle(panel, "top", `${position.top}px`);
    this.renderer.setStyle(panel, "left", `${position.left}px`);
  }

  private getBelowTriggerPosition(trigger: HTMLElement, panelRect: DOMRect): { top: number; left: number } {
    const triggerRect = trigger.getBoundingClientRect();
    const top = triggerRect.bottom + panelRect.height > window.innerHeight
      ? Math.max(0, triggerRect.top - panelRect.height)
      : triggerRect.bottom;
    const left = triggerRect.left + panelRect.width > window.innerWidth
      ? Math.max(0, window.innerWidth - panelRect.width)
      : triggerRect.left;
    return { top, left };
  }

  private getSideAnchoredPosition(trigger: HTMLElement, sideAnchor: HTMLElement, panelRect: DOMRect): { top: number; left: number } {
    const triggerRect = trigger.getBoundingClientRect();
    const anchorRect = sideAnchor.getBoundingClientRect();
    const left = anchorRect.right + panelRect.width > window.innerWidth
      ? Math.max(0, anchorRect.left - panelRect.width)
      : anchorRect.right;
    const top = Math.max(0, Math.min(triggerRect.top, window.innerHeight - panelRect.height));
    return { top, left };
  }

  // Tracks every panel currently visible (panel -> its trigger/sideAnchor) so open panels can be
  // re-aligned while the grid or the page scrolls or the window resizes.
  private trackOpenPanel(panel: HTMLElement, trigger: HTMLElement, sideAnchor?: HTMLElement): void {
    if (this.openPanels.size === 0) {
      window.addEventListener("scroll", this.repositionOpenPanels, true);
      window.addEventListener("resize", this.repositionOpenPanels);
    }
    this.openPanels.set(panel, { trigger, sideAnchor });
  }

  private untrackOpenPanel(panel: HTMLElement): void {
    this.openPanels.delete(panel);
    if (this.openPanels.size === 0) {
      window.removeEventListener("scroll", this.repositionOpenPanels, true);
      window.removeEventListener("resize", this.repositionOpenPanels);
    }
  }
}
